/**
 * Map of currency codes to their typical locales for proper formatting.
 * Intl.NumberFormat uses the locale to determine number formatting (decimal/thousand separators)
 * and the currency to determine the symbol.
 */
const CURRENCY_LOCALES: Record<string, string> = {
  USD: "en-US",
  AUD: "en-AU",
  NZD: "en-NZ",
  GBP: "en-GB",
  EUR: "de-DE",
  CAD: "en-CA",
  JPY: "ja-JP",
  CNY: "zh-CN",
  SGD: "en-SG",
  HKD: "en-HK",
};

export interface FormatCurrencyOptions {
  /** Show zero values instead of returning a placeholder */
  showZero?: boolean;
  /** Text to show for null/undefined values */
  placeholder?: string;
}

/**
 * Format a price value with the appropriate currency symbol and locale.
 *
 * @param value - The numeric value to format
 * @param currency - ISO 4217 currency code (e.g., "USD", "AUD", "EUR")
 * @param options - Additional formatting options
 * @returns Formatted currency string (e.g., "$1,234.56", "A$1,234.56", "€1.234,56")
 */
export function formatCurrency(
  value: number | null | undefined,
  currency: string = "USD",
  options?: FormatCurrencyOptions
): string {
  const { showZero = true, placeholder = "—" } = options ?? {};

  if (value == null) {
    return placeholder;
  }

  if (value === 0 && !showZero) {
    return placeholder;
  }

  const currencyCode = currency?.toUpperCase() || "USD";
  const locale = CURRENCY_LOCALES[currencyCode] || "en-US";

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currencyCode,
  }).format(value);
}

/**
 * Format a price value with locale override.
 * Useful for widget where locale is configurable.
 *
 * @param value - The numeric value to format
 * @param currency - ISO 4217 currency code
 * @param locale - Override locale (e.g., "en-US", "en-IE")
 */
export function formatPrice(
  value: number | null | undefined,
  currency?: string | null,
  locale?: string
): string {
  if (value == null) {
    return "—";
  }

  const currencyCode = currency?.toUpperCase() || "EUR";
  const resolvedLocale = locale || CURRENCY_LOCALES[currencyCode] || "en-US";

  return new Intl.NumberFormat(resolvedLocale, {
    style: "currency",
    currency: currencyCode,
  }).format(value);
}

/**
 * Format a number with locale-appropriate separators.
 */
export function formatNumber(
  value: number | null | undefined,
  locale: string = "en-US"
): string {
  if (value == null) {
    return "—";
  }

  return new Intl.NumberFormat(locale).format(value);
}

/**
 * Truncate a string to a maximum length with ellipsis.
 */
export function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) {
    return str;
  }
  return str.slice(0, maxLength - 1) + "…";
}

/**
 * Generate a unique ID for DOM elements.
 */
export function generateId(prefix: string = "apex"): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Debounce a function call.
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

/**
 * Escape HTML special characters to prevent XSS.
 */
export function escapeHtml(str: string): string {
  const div = document.createElement("div");
  div.textContent = str;
  return div.innerHTML;
}
